import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Card, Grid, Skeleton, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
import { SectionHeader } from '../common';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getAllCourses } from '../../../redux/slices/course';

// ----------------------------------------------------------------------

// This widget only renders inside the course detail page's quarter-width
// sidebar, where each tile gets roughly half a column (~230px on a wide
// screen). A vertical media card is the wrong shape at that width, so the card
// is a horizontal row: fixed thumbnail on the left, title and counts on the
// right. Two per row, capped so the grid always fills complete rows.
const CARD_SIZE = { xs: 12 };
const CARD_LIMIT = 4;

const THUMB_SIZE = 72;

const REVEAL_TRANSITION = { duration: 0.5, ease: [0.43, 0.13, 0.23, 0.96] };
const revealDelay = (index) => Math.min(index, 5) * 0.07;

const ThumbBox = styled('div')(({ theme }) => ({
  position: 'relative',
  width: THUMB_SIZE,
  height: THUMB_SIZE,
  flexShrink: 0,
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'transform 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
  },
  '@media (hover: hover)': {
    '.related-card:hover & img': { transform: 'scale(1.08)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& img': { transition: 'none' },
  },
}));

const TagLabel = styled(Typography)(({ theme }) => ({
  display: 'block',
  overflow: 'hidden',
  fontWeight: 700,
  fontSize: 10.5,
  lineHeight: 1,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  color: theme.palette.primary.main,
  mb: 0.6,
}));

// The title owns the single link for the whole card. It must stay
// `position: static` so its `::after` resolves against the card and stretches
// over the entire tile rather than just the heading text.
const CardTitle = styled(RouterLink)(({ theme }) => ({
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  color: 'inherit',
  textDecoration: 'none',
  transition: 'color 0.25s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 2,
  },
  '&:hover': { color: theme.palette.primary.main },
  '&:focus-visible::after': {
    content: '""',
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
    borderRadius: theme.shape.borderRadius,
  },
}));

// Purely decorative: `pointerEvents: 'none'` lets clicks fall through to the
// stretched title link underneath.
const Chevron = styled(Iconify)(({ theme }) => ({
  flexShrink: 0,
  color: theme.palette.primary.main,
  opacity: 0,
  transform: 'translateX(-4px)',
  transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
  '@media (hover: hover)': {
    '.related-card:hover &': { opacity: 1, transform: 'translateX(0)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

// ----------------------------------------------------------------------

CourseCard.propTypes = {
  course: PropTypes.object.isRequired,
  index: PropTypes.number,
};

function CourseCard({ course, index = 0 }) {
  const { slug, name, image, tag, videoCount, subjectCount } = course ?? {};
  const linkTo = `/course/${slug}`;

  // Only render the metrics the payload actually provides, so a partial
  // response never shows "undefined videos".
  const stats = [
    { id: 'videos', icon: 'ic:round-play-circle', value: videoCount, label: 'videos' },
    { id: 'subjects', icon: 'ic:round-menu-book', value: subjectCount, label: 'subjects' },
  ].filter((stat) => Number.isFinite(stat.value));

  return (
    <Grid size={CARD_SIZE} sx={{ display: 'flex' }}>
      <MotionInView
        variants={varFadeInUp}
        transition={{ ...REVEAL_TRANSITION, delay: revealDelay(index) }}
        sx={{ width: '100%', display: 'flex' }}
      >
        <Card
          className="related-card"
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.25,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: (theme) => theme.shape.borderRadius,
            boxShadow: 'none',
            transition: 'border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': {
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.45),
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                boxShadow: (theme) => `0 10px 26px ${alpha(theme.palette.grey[900], 0.08)}`,
              },
            },
          }}
        >
          <ThumbBox>
            {image ? (
              <Box component="img" alt={name || ''} src={image} loading="lazy" decoding="async" />
            ) : (
              <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.disabled' }}>
                <Iconify icon="ic:round-menu-book" width={26} />
              </Stack>
            )}
          </ThumbBox>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            {tag?.name && <TagLabel>{tag.name}</TagLabel>}

            <CardTitle
              component="h3"
              variant="subtitle2"
              to={linkTo}
              aria-label={`View course: ${name}`}
              sx={{ fontSize: 14.5, lineHeight: 1.35 }}
            >
              {name}
            </CardTitle>

            {stats.length > 0 && (
              <Stack direction="row" spacing={1.25} sx={{ mt: 0.75, flexWrap: 'wrap' }} useFlexGap>
                {stats.map((stat) => (
                  <Stack key={stat.id} direction="row" spacing={0.4} sx={{ alignItems: 'center' }}>
                    <Iconify icon={stat.icon} width={14} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
                      {`${stat.value} ${stat.label}`}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Chevron icon="eva:arrow-forward-outline" width={18} />
        </Card>
      </MotionInView>
    </Grid>
  );
}

// ----------------------------------------------------------------------

function RelatedCardSkeleton() {
  return (
    <Grid size={CARD_SIZE} sx={{ display: 'flex' }}>
      <Skeleton
        variant="rounded"
        width="100%"
        height={104}
        sx={{ borderRadius: (theme) => theme.shape.borderRadius }}
      />
    </Grid>
  );
}

// ----------------------------------------------------------------------

MoreCourseWidget.propTypes = {
  currentcourse: PropTypes.string,
  limit: PropTypes.number,
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  titleComponent: PropTypes.string,
  sx: PropTypes.object,
};

export default function MoreCourseWidget({
  currentcourse,
  limit = CARD_LIMIT,
  eyebrow = 'Keep exploring',
  title = 'More Courses',
  description,
  titleComponent = 'h2',
  sx,
}) {
  const dispatch = useDispatch();
  const { courses, isLoading } = useSelector((state) => state.course);

  useEffect(() => {
    dispatch(getAllCourses());
  }, [dispatch]);

  const moreCourses = useMemo(() => {
    const list = Array.isArray(courses) ? courses : courses?.results ?? [];
    return list.filter((course) => course?.slug && course.slug !== currentcourse).slice(0, limit);
  }, [courses, currentcourse, limit]);

  if (!moreCourses.length && isLoading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: Math.min(limit, 8) }, (_, index) => (
          <RelatedCardSkeleton key={index} />
        ))}
      </Grid>
    );
  }

  if (!moreCourses.length) {
    return null;
  }

  return (
    <Box component="section" sx={{ width: '100%', ...sx }}>
      <SectionHeader
        eyebrow={eyebrow}
        title={title}
        titleComponent={titleComponent}
        description={description}
        align="left"
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
        {moreCourses.map((course, index) => (
          <CourseCard key={course.slug} course={course} index={index} />
        ))}
      </Grid>
    </Box>
  );
}
